/**
 * The nine steps, in order, and the URL they live at.
 *
 * The wizard's whole position is two query parameters — `?request=<id>&step=<n>` — and nothing
 * else. A refresh, a bookmark or a link pasted into Slack all resume at exactly the same place,
 * which is what makes "Save & exit" a real exit rather than a lost afternoon.
 */

export interface WizardStep {
	index: number;
	/** Stable slug, used for React keys and for nothing the user ever sees. */
	key: string;
	/** What the left rail calls it. Client-facing copy, taken from UX §03. */
	label: string;
}

export const WIZARD_STEPS: WizardStep[] = [
	{ index: 0, key: "start", label: "Start" },
	{ index: 1, key: "basics", label: "Campaign Basics" },
	{ index: 2, key: "agents", label: "Agents" },
	{ index: 3, key: "markets", label: "Markets & Schedule" },
	{ index: 4, key: "leads", label: "Leads" },
	{ index: 5, key: "outcomes", label: "Outcomes" },
	{ index: 6, key: "compliance", label: "Compliance" },
	{ index: 7, key: "commercials", label: "Commercials" },
	{ index: 8, key: "review", label: "Review & Confirm" },
];

/** Step 0 is "Start" and is not counted, so the client reads "Step 1 of 8" through "Step 8 of 8". */
export const LAST_STEP = 8;

export const WIZARD_BASE_PATH = "/campaign-requests/create";
export const REQUESTS_PATH = "/campaign-requests";

/** A step number that is out of range, missing or not a number is step 0, never a blank screen. */
export function parseStep(raw: string | null): number {
	const parsed = Number.parseInt(raw ?? "", 10);
	if (!Number.isFinite(parsed)) return 0;
	return Math.min(Math.max(parsed, 0), LAST_STEP);
}

/** The link that resumes this request at this step. */
export function wizardPath(requestId: string | null, step: number): string {
	const search = new URLSearchParams();
	if (requestId) search.set("request", requestId);
	search.set("step", String(step));
	return `${WIZARD_BASE_PATH}?${search.toString()}`;
}
